import { useMemo, useState } from 'react';
import { difference } from 'lodash-es';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { SOrganGroup } from '@lp/features/Scheduler/SchedulerSurgeons/types';
import { InsertSurgeonToRegionList } from '@lp/features/modals/InsertSurgeonToRegionModal/components';
import { Region, SResource, SResourcesWithShifts } from '@/api/apis/resources/schedule';
import { OrganType } from '@/api/apis/case';

type InsertSurgeonToRegionModalProps = {
  region: Region;
  organGroup: SOrganGroup;
  userList: SResource[];
  shiftsData: SResourcesWithShifts;
  regionsDictionary: Record<string, Region>;
  onClose: () => void;
  onSave: (userIds: string[]) => void;
};

export const InsertSurgeonToRegionModal = ({
  region,
  organGroup,
  userList,
  shiftsData,
  regionsDictionary,
  onClose,
  onSave,
}: InsertSurgeonToRegionModalProps) => {
  const userIds = useMemo(() => userList.map((user) => user.id), [userList]);
  const [selectedIds, setSelectedIds] = useState<string[]>(userIds);
  const fullResourceList = useMemo(() => {
    return Object.values(shiftsData)
      .map((r) => r.resource)
      .filter((r) => {
        const organs = new Set(r.organs);
        return organGroup === SOrganGroup.Liver
          ? organs.has(OrganType.Liver)
          : !organs.has(OrganType.Liver) && (organs.has(OrganType.Heart) || organs.has(OrganType.Lungs));
      });
  }, [organGroup, shiftsData]);

  const handleSave = () => onSave(difference(selectedIds, userIds));

  return (
    <Modal
      isOpen
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80 m-2!',
        header: 'text-hl! font-secondary p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
      }}
      size="4xl"
      backdrop="opaque"
      onClose={onClose}
      scrollBehavior="inside"
      hideCloseButton
    >
      <ModalContent>
        <ModalHeader>Assign Surgeon to {region.name}</ModalHeader>
        <ModalBody>
          <div className="grow">
            <InsertSurgeonToRegionList
              regionId={region.id}
              resourceList={fullResourceList}
              regionsDictionary={regionsDictionary}
              insertedIds={userIds}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
            />
          </div>
        </ModalBody>
        <ModalFooter className="justify-end">
          <div className="flex gap-3">
            <Button variant="outline" size="md" className="min-w-[160px]" onClick={onClose}>
              Discard
            </Button>
            <Button size="md" className="min-w-[160px]" onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
